import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { db, storage, auth } from "../firebase";
import { signInAnonymously } from "firebase/auth";
import dayjs from "dayjs";
import {
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import templates from "../data/templates";
import { findEventByCodeOrSlug } from "../utils/eventLookup";
import { moderateText, moderateImageUpload } from "../utils/moderation";

const MAX_IMAGE_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

const getCompressedName = (name) => {
  const baseName = name.replace(/\.[^/.]+$/, "");
  return `${baseName || "photo"}.jpg`;
};

const compressImageFile = async (file) => {
  if (!file.type.startsWith("image/") || file.type === "image/gif") {
    return file;
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImage(objectUrl);
    const scale = Math.min(
      1,
      MAX_IMAGE_DIMENSION / Math.max(image.width, image.height)
    );
    const canvas = document.createElement("canvas");

    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);
    canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY)
    );

    if (!blob || blob.size >= file.size) return file;

    return new File([blob], getCompressedName(file.name), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (err) {
    console.warn("Could not compress image; using original file.", err);
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const getUploadErrorMessage = (err) => {
  if (err?.code === "storage/unauthorized") {
    return "The photo upload was blocked by storage permissions.";
  }

  if (err?.code === "storage/retry-limit-exceeded") {
    return "The photo upload timed out.";
  }

  if (err?.code === "storage/canceled") {
    return "The photo upload was cancelled.";
  }

  return err?.message || "The photo upload failed.";
};

const ensureSignedIn = async () => {
  if (auth.currentUser) {
    return auth.currentUser;
  }

  const credential = await signInAnonymously(auth);
  return credential.user;
};

const isEventOpen = (event) => {
  const eventDate = event?.date?.toDate ? dayjs(event.date.toDate()) : null;
  return eventDate
    ? eventDate.isSame(dayjs(), "day") || eventDate.isAfter(dayjs(), "day")
    : true;
};

const GuestFormPage = () => {
  const [form, setForm] = useState({ name: "", message: "" });
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [event, setEvent] = useState(null);
  const { shortCode } = useParams();
  const [userId, setUserId] = useState(null);
  const [eventId, setEventId] = useState(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    const fetchEvent = async () => {
      const result = await findEventByCodeOrSlug(shortCode);
      if (result) {
        setEvent(result.event);
        setUserId(result.userId);
        setEventId(result.eventId);
      }
    };
    fetchEvent();
  }, [shortCode]);

  const clearPreview = () => {
    setPreviewUrl((currentUrl) => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      return "";
    });
  };

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    clearPreview();
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    e.target.value = "";
  };

  const resetSubmitState = () => {
    setUploading(false);
    setUploadStatus("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) return;

    if (!isEventOpen(event)) {
      alert("This event has passed, so new messages are now closed.");
      return;
    }

    setUploading(true);
    let currentStep = "checking message";
    const updateStatus = (status) => {
      currentStep = status.toLowerCase();
      setUploadStatus(status);
    };

    updateStatus("Checking message...");

    try {
      updateStatus("Signing in...");
      await ensureSignedIn();

      updateStatus("Checking message...");
      const moderation = await moderateText(form.message);
      let moderationStatus = "approved";
      let moderationReason = "";
      if (!moderation.ok) {
        moderationStatus = "needsReview";
        moderationReason = "text_moderation_unavailable";
      }

      if (!moderation.safe) {
        moderationStatus = "needsReview";
        moderationReason = "text_flagged";
      }

      let imageUrl = "";
      let imageName = "";

      if (imageFile) {
        if (!userId || !eventId) {
          alert("The event is still loading. Please try again in a moment.");
          resetSubmitState();
          return;
        }

        updateStatus("Preparing photo...");
        const preparedImage = await compressImageFile(imageFile);
        const fileName = `${Date.now()}_${preparedImage.name}`;
        const fileRef = ref(
          storage,
          `users/${userId}/events/${eventId}/uploads/${fileName}`
        );

        updateStatus("Uploading photo...");
        try {
          await uploadBytes(fileRef, preparedImage, {
            contentType: preparedImage.type || "image/jpeg",
          });
        } catch (err) {
          if (preparedImage === imageFile) {
            throw err;
          }

          console.warn("Compressed image upload failed; retrying original.", err);
          updateStatus("Retrying photo upload...");
          await uploadBytes(fileRef, imageFile, {
            contentType: imageFile.type || "image/jpeg",
          });
        }

        imageUrl = await getDownloadURL(fileRef);
        imageName = fileName;

        updateStatus("Checking photo...");
        const imageModeration = await moderateImageUpload(imageUrl);
        if (!imageModeration.safe) {
          moderationStatus = "needsReview";
          moderationReason = "image_flagged";
        }

        if (!imageModeration.ok) {
          console.warn("Image moderation unavailable; allowing image.");
          if (moderationStatus === "approved") {
            moderationStatus = "needsReview";
            moderationReason = "image_moderation_unavailable";
          }
        }
      }

      updateStatus("Saving message...");
      await addDoc(
        collection(db, "users", userId, "events", eventId, "messages"),
        {
          ...form,
          imageUrl,
          imageName,
          moderationStatus,
          moderationReason,
          timestamp: serverTimestamp(),
        }
      );

      setForm({ name: "", message: "" });
      setImageFile(null);
      clearPreview();
      resetSubmitState();
      setSubmitted(true);
    } catch (err) {
      console.error("Submit error:", err);
      const uploadError =
        currentStep.includes("photo upload") || currentStep.includes("uploading")
          ? ` ${getUploadErrorMessage(err)}`
          : "";
      alert(
        `Sorry, something went wrong while ${currentStep}.${uploadError} Please try again.`
      );
      resetSubmitState();
    }
  };

  const selectedTemplate = templates.find((t) => t.id === event?.templateId);
  const accent =
    event?.displaySettings?.accentColor ||
    selectedTemplate?.accentColor ||
    "#ec4899";
  const eventOpen = isEventOpen(event);

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 ${
        selectedTemplate?.background || "bg-gradient-to-br from-pink-100 to-pink-200"
      }`}
    >
      {event && !eventOpen ? (
        <div
          className="bg-white p-6 rounded-xl shadow-md w-full max-w-md text-center space-y-4"
          style={{ borderTop: `4px solid ${accent}` }}
        >
          <h2 className="text-2xl font-bold">Messages are closed</h2>
          <p className="text-gray-600">
            This event has passed, so new photo and message submissions are no
            longer being accepted.
          </p>
        </div>
      ) : submitted ? (
        <div
          className="bg-white p-6 rounded-xl shadow-md w-full max-w-md text-center space-y-4"
          style={{ borderTop: `4px solid ${accent}` }}
        >
          <h2 className="text-2xl font-bold">Thanks for your message</h2>
          <p className="text-gray-600">
            Your photo or note has been added to the event.
          </p>
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            style={{ backgroundColor: accent }}
            className="w-full py-2 text-white font-semibold rounded hover:opacity-90"
          >
            Send Another
          </button>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="bg-white p-6 rounded-xl shadow-md w-full max-w-md space-y-4 text-gray-900"
          style={{ borderTop: `4px solid ${accent}` }}
        >
        <h2 className="text-2xl font-bold text-center mb-4">
          Leave a Message
        </h2>

        <div>
          <label className="block font-semibold mb-1">Your Name</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleInputChange}
            required
            className="w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Your Message</label>
          <textarea
            name="message"
            value={form.message}
            onChange={handleInputChange}
            rows="4"
            required
            className="w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Optional Image</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading}
              className="w-full border rounded p-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60"
            >
              Take Photo
            </button>
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              disabled={uploading}
              className="w-full border rounded p-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60"
            >
              Choose Photo
            </button>
          </div>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleImageUpload}
            disabled={uploading}
            className="hidden"
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={uploading}
            className="hidden"
          />
          {imageFile && (
            <p className="mt-2 text-sm text-gray-600">{imageFile.name}</p>
          )}
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Preview"
              className="mt-2 rounded border max-h-48 object-cover"
            />
          )}
        </div>

        {uploadStatus && (
          <div
            role="status"
            aria-live="polite"
            className="rounded border border-pink-100 bg-pink-50 px-3 py-2 text-sm font-medium text-pink-900"
          >
            {uploadStatus}
          </div>
        )}

        <button
          type="submit"
          disabled={
            uploading ||
            !form.name ||
            !form.message ||
            !userId ||
            !eventId ||
            !eventOpen
          }
          style={{ backgroundColor: accent }}
          className="w-full py-2 text-white font-semibold rounded hover:opacity-90 disabled:opacity-60"
        >
          {uploading ? "Sending..." : "Send Message"}
        </button>
        </form>
      )}
    </div>
  );
};

export default GuestFormPage;
